import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { expenseService } from "@/services/expenseService";
import { ExpenseType } from "@/types/db/expenses";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef, RowAction, EditableOption } from "@/components/NewTable";
import { theme } from "@/constants/themes";
import { formatCurrency as formatCurrencyLib } from "@/lib/format/currency";

// Status color mapping
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral">> = {
  PENDING: "pending",
  APPROVED: "complete",
  REJECTED: "attention",
};

// Billing status color mapping (matches billing_items pattern)
const BILLING_STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "todo" | "neutral">> = {
  PENDING: "neutral",
  "READY FOR BILLING": "todo",
  "BILLING IN PROGRESS": "pending",
  "PNL PENDING": "complete",
};

export function RelatedExpensesScreen() {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { displayNames } = useAuth();
  const { expenses: expensePermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [expenses, setExpenses] = useState<ExpenseType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.expenses?.plural || "Expenses"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.expenses?.plural]);

  useEffect(() => {
    const fetchExpenses = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedExpenses: ExpenseType[] = [];

        switch (currentEntity.currentPanel) {
          case "project":
            fetchedExpenses = await expenseService.getExpensesByProjectId(currentEntity.currentId);
            break;
          case "shift":
            fetchedExpenses = await expenseService.getExpensesByShiftId(currentEntity.currentId);
            break;
          default:
            console.warn(`No expense relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setExpenses(fetchedExpenses);
      } catch (err: any) {
        console.error("Failed to fetch related expenses:", err);
        setError(err.message || "Failed to load expenses");
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status?.toUpperCase() || "PENDING"] || "pending";
    return colors[colorKey].border;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getBillingStatusColor = (status?: string) => {
    const colorKey = BILLING_STATUS_COLORS[status?.toUpperCase() || "PENDING"] || "neutral";
    return colors[colorKey].border;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formatCurrency = (amount?: string | number | null) => formatCurrencyLib(amount, { blank: "—" });

  // Status options for editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "PENDING", label: "Pending", color: colors.pending.border },
      { value: "APPROVED", label: "Approved", color: colors.complete.border },
      { value: "REJECTED", label: "Rejected", color: colors.attention.border },
    ],
    [colors],
  );

  // Handle edit callback
  const handleEdit = useCallback(async (row: ExpenseType, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      await expenseService.updateExpense(row.id, { [key]: newValue });
      setExpenses((prev) => prev.map((exp) => (exp.id === row.id ? { ...exp, [key]: newValue } : exp)));
    } catch (err) {
      console.error("Failed to update expense:", err);
    }
  }, []);

  // Handle delete callback
  const handleDelete = useCallback(async (row: ExpenseType) => {
    if (!row.id) return;

    try {
      await expenseService.deleteExpense(row.id);
      setExpenses((prev) => prev.filter((exp) => exp.id !== row.id));
    } catch (err) {
      console.error("Failed to delete expense:", err);
    }
  }, []);

  const handleExpensePress = useCallback(
    (expense: ExpenseType) => {
      // Save current entity to navigation history before navigating away
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }

      // Navigate to the expense
      navigateToEntity("expense", expense.id!);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const handleProjectPress = useCallback(
    (expense: ExpenseType) => {
      if (!(expense as any).project?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("project", (expense as any).project.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const rowActions: RowAction<ExpenseType>[] = useMemo(
    () => [
      {
        icon: "chevron-right",
        onPress: (row) => handleExpensePress(row),
        label: "View",
      },
    ],
    [handleExpensePress],
  );

  // Define columns for the table
  const columns: ColumnDef<ExpenseType>[] = useMemo(() => {
    const cols: ColumnDef<ExpenseType>[] = [];

    // Expense number column
    cols.push({
      key: "expense_number",
      header: "Number",
      width: 100,
      accessor: (row: any) => (row.expense_number ? `#${row.expense_number}` : "—"),
    });

    // Description column
    cols.push({
      key: "description",
      header: "Description",
      flex: 1,
      minWidth: 150,
      accessor: (row: any) => row.description || "—",
    });

    // Status column with editable picker
    cols.push({
      key: "status",
      header: "Status",
      width: 120,
      align: "center",
      searchable: true,
      searchValue: (_, row) => row.status || "PENDING",
      editable: {
        type: "picker",
        options: statusOptions,
        placeholder: "Select status",
      },
      render: (value, row) => (
        <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
          <ThemedText style={styles.statusText}>{row.status?.toUpperCase() || "PENDING"}</ThemedText>
        </View>
      ),
    });

    // Amount column
    cols.push({
      key: "amount",
      header: "Amount",
      width: 120,
      align: "right",
      searchable: false,
      accessor: (row: any) => row.amount,
      render: (value) => (
        <ThemedText style={styles.cellText}>{formatCurrency(value as string | number | null)}</ThemedText>
      ),
    });

    // Date column
    cols.push({
      key: "created_at",
      header: "Date",
      width: 100,
      searchable: false,
      accessor: (row: any) => row.created_at,
      render: (value) => <ThemedText style={styles.cellText}>{value ? formatDate(value) : "—"}</ThemedText>,
    });

    // Billing status column (read-only, from project)
    cols.push({
      key: "billing_status",
      header: "Billing Status",
      width: 170,
      align: "center",
      searchable: true,
      searchValue: (_, row) => (row as any).billing_status || "",
      accessor: (row: any) => row.billing_status || "—",
      render: (_value, row) => {
        const bs = (row as any).billing_status;
        if (!bs) return <ThemedText style={styles.cellText}>—</ThemedText>;
        return (
          <View style={[styles.statusPill, { backgroundColor: getBillingStatusColor(bs) }]}>
            <ThemedText style={styles.statusText}>{bs.toUpperCase()}</ThemedText>
          </View>
        );
      },
    });

    // Project column (hidden when viewing from project panel)
    if (currentEntity?.currentPanel !== "project") {
      cols.push({
        key: "project",
        header: displayNames?.projects?.name || "Project",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.project?.name || "—",
        onPress: (row) => handleProjectPress(row),
      });
    }

    return cols;
  }, [
    statusOptions,
    formatDate,
    formatCurrency,
    getStatusColor,
    getBillingStatusColor,
    handleProjectPress,
    currentEntity?.currentPanel,
    displayNames?.projects?.name,
  ]);

  const expensesName = displayNames?.expenses?.plural?.toLowerCase() || "expenses";

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {expensesName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<ExpenseType>
        data={expenses}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        rowActions={rowActions}
        onEdit={handleEdit}
        canEdit={expensePermissions.update}
        onDelete={handleDelete}
        canDelete={expensePermissions.delete}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "created_at", direction: "desc" }}
        emptyMessage={`No ${expensesName} found`}
        emptyIcon="cash-multiple"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  cellText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
